import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { usePushNotifications } from '../hooks/usePushNotifications';
import api from '../utils/api';
import { LogOut, Building2, Phone, Shield, Smartphone, ChevronRight, Download, Lock, Eye, EyeOff, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_LABEL = { admin: 'Administrator', benutzer: 'Benutzer', mitarbeiter: 'Mitarbeiter' };

const NOTIF_TYPES = [
  { key: 'notify_damage',   emoji: '⚠️', label: 'Neue Schadensmeldungen',  sub: 'Sofort wenn ein Schaden erfasst wird' },
  { key: 'notify_calendar', emoji: '📅', label: 'Neue Kalendertermine',    sub: 'Bei neuen Einträgen im Kalender' },
  { key: 'notify_tuev',     emoji: '🔧', label: 'TÜV-Fälligkeiten',       sub: '60 und 30 Tage vor dem TÜV' },
];

function Toggle({ value, onChange, disabled, loading }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      {loading && <div className="spinner" style={{ width: 16, height: 16 }} />}
      <button
        type="button"
        onClick={() => !disabled && !loading && onChange(!value)}
        disabled={disabled || loading}
        role="switch"
        aria-checked={value}
        style={{
          width: 52, height: 30,
          borderRadius: 15,
          border: '2px solid',
          borderColor: value ? '#16a34a' : 'var(--border)',
          background: value ? '#16a34a' : 'var(--bg-input)',
          position: 'relative',
          cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
          transition: 'background 280ms, border-color 280ms',
          opacity: disabled ? .45 : 1,
          flexShrink: 0,
          padding: 0,
          outline: 'none',
        }}>
        <div style={{
          position: 'absolute',
          top: 2,
          left: value ? 24 : 2,
          width: 22,
          height: 22,
          background: value ? '#fff' : 'var(--text-faint)',
          borderRadius: '50%',
          transition: 'left 280ms cubic-bezier(.4,0,.2,1), background 280ms',
          boxShadow: '0 1px 4px rgba(0,0,0,.3)',
        }} />
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {title && (
        <div style={{
          fontSize: '.68rem', fontWeight: 700, color: 'var(--text-muted)',
          letterSpacing: '.1em', textTransform: 'uppercase',
          padding: '0 4px', marginBottom: 8,
        }}>
          {title}
        </div>
      )}
      <div className="card">{children}</div>
    </div>
  );
}

function Row({ emoji, title, sub, right, onClick, danger, border = true }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderBottom: border ? '1px solid var(--border)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 120ms',
        userSelect: 'none',
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background = 'var(--bg)')}
      onMouseLeave={e => (e.currentTarget.style.background = '')}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: danger ? 'var(--red-bg)' : 'var(--bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: '1.1rem',
      }}>
        {emoji}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '.88rem', fontWeight: 600,
          color: danger ? 'var(--red)' : 'var(--text)',
        }}>
          {title}
        </div>
        {sub && (
          <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
            {sub}
          </div>
        )}
      </div>
      {right !== undefined ? right : onClick && <ChevronRight size={16} color="var(--text-faint)" />}
    </div>
  );
}

export default function Einstellungen() {
  const { user, logout, can } = useAuth();
  const nav = useNavigate();
  const { permission, pushActive, installed, enable, disable } = usePushNotifications();

  const [pushLoading, setPushLoading] = useState(false);

  // Passwort ändern
  const [pwForm, setPwForm]   = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwShow, setPwShow]   = useState({ current: false, newPw: false, confirm: false });
  const [pwSuccess, setPwSuccess] = useState(false);
  const [prefs, setPrefs] = useState({ notify_damage: true, notify_calendar: true, notify_tuev: true });
  const [prefLoading, setPrefLoading] = useState({});

  // Passwort ändern
  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current) { toast.error('Aktuelles Passwort eingeben'); return; }
    if (!pwForm.newPw || pwForm.newPw.length < 6) { toast.error('Neues Passwort mind. 6 Zeichen'); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: pwForm.current,
        new_password:     pwForm.newPw,
      });
      toast.success('Passwort erfolgreich geändert');
      setPwForm({ current: '', newPw: '', confirm: '' });
      setPwSuccess(true);
      setTimeout(() => setPwSuccess(false), 3000);
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler beim Ändern');
    } finally { setPwLoading(false); }
  };

  // Notification Prefs laden
  useEffect(() => {
    api.get('/notifications/settings')
      .then(r => {
        setPrefs({
          notify_damage:   r.data.notify_damage   !== false,
          notify_calendar: r.data.notify_calendar !== false,
          notify_tuev:     r.data.notify_tuev     !== false,
        });
      })
      .catch(() => {});
  }, []);

  const handlePushToggle = async (val) => {
    if (pushLoading) return;
    setPushLoading(true);
    try {
      if (val) {
        const result = await enable();
        if (result.ok) {
          if (result.mode === 'webpush') {
            toast.success('✅ Push aktiviert — auch bei gesperrtem Bildschirm!');
          } else {
            toast.success('✅ Push aktiviert (Polling-Modus)');
          }
        } else if (result.reason === 'denied') {
          toast.error('Berechtigung verweigert — in Geräteeinstellungen erlauben');
        } else {
          toast.error('Push konnte nicht aktiviert werden');
        }
      } else {
        await disable();
        toast.success('Push deaktiviert');
      }
    } catch(e) {
      toast.error('Fehler: ' + (e?.message || 'Unbekannter Fehler'));
    } finally {
      setPushLoading(false);
    }
  };

  const handlePrefToggle = async (key, val) => {
    if (prefLoading[key]) return;
    setPrefLoading(p => ({ ...p, [key]: true }));
    const prev = prefs[key];
    setPrefs(p => ({ ...p, [key]: val }));
    try {
      await api.put('/notifications/settings', { [key]: val });
    } catch {
      setPrefs(p => ({ ...p, [key]: prev }));
      toast.error('Fehler beim Speichern');
    } finally {
      setPrefLoading(p => ({ ...p, [key]: false }));
    }
  };

  // Push-Status-Text
  let pushSub = '';
  let pushSubColor = 'var(--text-muted)';
  if (!('Notification' in window)) {
    pushSub = 'Browser unterstützt keine Push-Notifications';
    pushSubColor = 'var(--text-muted)';
  } else if (permission === 'denied') {
    pushSub = '⛔ Berechtigung verweigert — Geräteeinstellungen → App → Mitteilungen';
    pushSubColor = 'var(--red)';
  } else if (pushActive) {
    pushSub = '✓ Aktiv — Benachrichtigungen auch bei gesperrtem Bildschirm';
    pushSubColor = 'var(--green)';
  } else if (!installed) {
    pushSub = '⚠️ App auf Homescreen installieren für Hintergrund-Push';
    pushSubColor = 'var(--yellow)';
  } else if (permission === 'granted') {
    pushSub = 'Berechtigung erteilt — Toggle aktivieren';
    pushSubColor = 'var(--text-muted)';
  } else {
    pushSub = 'Toggle aktivieren um Berechtigung anzufragen';
    pushSubColor = 'var(--text-muted)';
  }

  return (
    <Layout title="Einstellungen">

      {/* Profil */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 54, height: 54, borderRadius: '50%',
            background: 'linear-gradient(135deg,#e63946,#c1121f)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '1.25rem', fontWeight: 800, flexShrink: 0,
            boxShadow: '0 4px 14px rgba(230,57,70,.3)',
          }}>
            {(user?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem' }}>{user?.name}</div>
            <div style={{ fontSize: '.78rem', color: 'var(--accent)', fontWeight: 700, marginTop: 3 }}>
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{user?.email}</div>
          </div>
        </div>
      </div>

      {/* iOS Installationshinweis */}
      {!installed && (
        <div style={{
          marginBottom: 16, padding: '14px 16px',
          background: 'var(--yellow-bg)', borderRadius: 12,
          border: '1px solid rgba(217,119,6,.25)',
        }}>
          <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--yellow)', marginBottom: 6 }}>
            📲 App installieren für Hintergrund-Push
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <strong>iPhone (iOS 16.4+):</strong> Safari → Teilen ↑ → „Zum Home-Bildschirm"<br />
            <strong>Android:</strong> Chrome → Menü ⋮ → „App installieren"<br />
            Dann App vom Homescreen öffnen und Push aktivieren.
          </div>
        </div>
      )}

      {/* Push */}
      <Section title="Benachrichtigungen">
        <Row
          emoji={pushActive ? '🔔' : '🔕'}
          title="Push-Benachrichtigungen"
          sub={<span style={{ color: pushSubColor }}>{pushSub}</span>}
          border={false}
          right={
            <Toggle
              value={pushActive}
              onChange={handlePushToggle}
              disabled={permission === 'denied' || !('Notification' in window)}
              loading={pushLoading}
            />
          }
        />
      </Section>

      {/* Benachrichtigung Typen */}
      <Section title="Benachrichtige mich bei">
        {NOTIF_TYPES.map(({ key, emoji, label, sub }, i) => (
          <Row
            key={key}
            emoji={emoji}
            title={label}
            sub={sub}
            border={i < NOTIF_TYPES.length - 1}
            right={
              <Toggle
                value={prefs[key]}
                onChange={(val) => handlePrefToggle(key, val)}
                disabled={!pushActive}
                loading={!!prefLoading[key]}
              />
            }
          />
        ))}
        {!pushActive && (
          <div style={{
            padding: '10px 16px 14px',
            fontSize: '.75rem', color: 'var(--text-muted)',
            borderTop: '1px solid var(--border)', fontStyle: 'italic',
          }}>
            Push-Benachrichtigungen aktivieren um Typen einzeln einzustellen
          </div>
        )}
      </Section>

      {/* App */}
      <Section title="App">
        <Row
          emoji="📱"
          title="PWA-Status"
          sub={installed ? '✓ Als Homescreen-App installiert' : 'Im Browser geöffnet (eingeschränkte Push-Funktion)'}
          right={
            <span style={{ fontSize: '.72rem', fontWeight: 700, color: installed ? 'var(--green)' : 'var(--yellow)' }}>
              {installed ? 'Installiert' : 'Browser'}
            </span>
          }
          border
        />
        <Row
          emoji="🔐"
          title="Push-Status"
          sub={`Berechtigung: ${permission || 'unbekannt'} · Subscription: ${pushActive ? 'aktiv' : 'inaktiv'}`}
          right={null}
          border={false}
        />
      </Section>

      {/* Admin: Backup */}
      {can('benutzer_manage') && (
        <Section title="Administration">
          <Row
            emoji="💾"
            title="Datensicherung"
            sub="Backup erstellen und herunterladen"
            onClick={() => nav('/admin/backup')}
            border={false}
          />
        </Section>
      )}

      {/* Firma */}
      <Section title="Getränkefachhandel Gerhards">
        <Row emoji="🏢" title="Bruch 1, 41334 Nettetal" border />
        <Row emoji="📞" title="02157 / 13 05 92" sub="Büro" border />
        <Row emoji="📞" title="0172 / 256 47 76" sub="24h Notfall" border={false} />
      </Section>


      {/* Passwort ändern */}
      <Section title="Passwort ändern">
        <form onSubmit={changePassword} style={{ padding: '4px 0' }}>
          {[
            { key: 'current', label: 'Aktuelles Passwort', ph: 'Aktuelles Passwort', ac: 'current-password' },
            { key: 'newPw',   label: 'Neues Passwort',     ph: 'Mind. 6 Zeichen',    ac: 'new-password' },
            { key: 'confirm', label: 'Passwort bestätigen',ph: 'Nochmal eingeben',    ac: 'new-password' },
          ].map(({ key, label, ph, ac }) => (
            <div key={key} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
              <label className="f-label" style={{ marginBottom: 6 }}>{label}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="f-input"
                  type={pwShow[key] ? 'text' : 'password'}
                  value={pwForm[key]}
                  onChange={e => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={ph}
                  required
                  autoComplete={ac}
                  style={{ paddingRight: 42, fontSize: 16 }}
                />
                <button type="button"
                  onClick={() => setPwShow(p => ({ ...p, [key]: !p[key] }))}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                  {pwShow[key] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          ))}
          <div style={{ padding: '12px 16px' }}>
            <button type="submit" className="btn btn-primary" disabled={pwLoading}
              style={{ width: '100%', justifyContent: 'center', padding: '12px', borderRadius: 10 }}>
              {pwLoading
                ? <><span className="spinner" style={{ width: 15, height: 15, borderWidth: 2 }} /> Ändern...</>
                : pwSuccess
                  ? <><Check size={14} /> Passwort geändert!</>
                  : <><Lock size={14} /> Passwort ändern</>
              }
            </button>
          </div>
        </form>
      </Section>

      {/* Konto */}
      <Section title="Konto">
        <Row
          emoji="🚪"
          title="Abmelden"
          danger
          onClick={() => { if (window.confirm('Wirklich abmelden?')) { logout(); nav('/login'); } }}
          border={false}
          right={null}
        />
      </Section>
    </Layout>
  );
}
